import { useFormikContext } from 'formik';
import { useNavigate } from 'react-router-dom';

import { Box } from '@hivebrite/design-system/Box';
import { Button } from '@hivebrite/design-system/Button';
import {
  FontAwesomeIcon,
  CATEGORIES,
} from '@hivebrite/design-system/FontAwesomeIcon';

import { useEditableContext } from 'hb-react/frontoffice/apps/Forum/contexts/EditableContext';
import { useForumContext } from 'hb-react/frontoffice/apps/Forum/contexts/ForumContext';
import type { FormValues } from 'hb-react/frontoffice/apps/Forum/types';
import { NotifyPeopleTriggerButton } from 'hb-react/frontoffice/components/NotifyPeopleModal/NotifyPeopleTriggerButton';
import UserTargeting from 'hb-react/frontoffice/components/user_targeting';
import { useTopicContext } from 'hb-react/frontoffice/contexts/TopicContext';
import { useDefaultAppContexts } from 'hb-react/frontoffice/contexts/withDefaultAppContexts';
import { isAnEmptyHtmlContent } from 'helpers/isAnEmptyHtmlContent';

interface CreateEditButtonsProps {
  isUploading: boolean;
  showUserTargetingButton: boolean;
}

const CreateEditButtons = ({
  isUploading,
  showUserTargetingButton,
}: CreateEditButtonsProps) => {
  const { appPageConfig } = useForumContext();
  const { currentUser } = useDefaultAppContexts();
  const { contentEditableRef } = useEditableContext();
  const { setFieldValue, values, isValid } = useFormikContext<FormValues>();
  const { topicId } = useTopicContext();
  const navigate = useNavigate();

  const maxTargetedUsers = currentUser?.user_targeting?.max_targeted_users;

  const isEmptyContent =
    !values.content &&
    // @ts-expect-error quill typing
    isAnEmptyHtmlContent(contentEditableRef?.current?.getHtml() || '');
  const isEmptyTitle = !values.title;
  const isCategoryFieldMandatory =
    appPageConfig?.config?.set_category_field_mandatory;
  const isEmptyCategories = isCategoryFieldMandatory
    ? values.category_ids.length === 0
    : false;

  const isDisabledSubmit =
    !isValid || isEmptyContent || isEmptyTitle || isEmptyCategories;

  return (
    <Box
      width="100%"
      display="flex"
      textAlign="right"
      justifyContent="flex-end"
      mt={6}
    >
      {showUserTargetingButton ? (
        <Box>
          <UserTargeting
            renderTriggerElement={(onClick) => (
              <NotifyPeopleTriggerButton values={values} onClick={onClick} />
            )}
            openByClickOn={
              <Button
                aria-hidden="true"
                as="span"
                dataTestId="user-targeting-button"
                icon={() => (
                  <FontAwesomeIcon
                    iconName="bell"
                    height="16px"
                    iconType={CATEGORIES.SOLID}
                  />
                )}
                variant="secondary"
              >
                {__('Notify people')}
                {values.targeted_user_ids &&
                  values.targeted_user_ids.length > 0 &&
                  ` · ${values.targeted_user_ids.length}`}
              </Button>
            }
            defaultQuery={{
              targeting_type: 'Posts::ForumPost',
              topic_ids: [topicId],
            }}
            maxTargetedUsers={maxTargetedUsers}
            onValidateSelection={(_selectedUsers, selectedUserIds) => {
              setFieldValue('targeted_user_ids', selectedUserIds);
            }}
            onCancelSelection={() => setFieldValue('targeted_user_ids', [])}
          />
        </Box>
      ) : (
        <Button variant="secondary" onClick={() => navigate('..')}>
          {__('Cancel')}
        </Button>
      )}
      <Box pl={3}>
        <Button
          isDisabled={isDisabledSubmit}
          isLoading={isUploading}
          type="submit"
        >
          {s__('Forum|Publish')}
        </Button>
      </Box>
    </Box>
  );
};

export default CreateEditButtons;
